import React, { useState, useEffect } from 'react';
import { Bell, Mail, ChevronRight, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Switch } from './ui/switch';
import { toast } from 'sonner';
import api from '../services/api';

const API_URL = '';

export default function ReminderPopup({ open, onOpenChange }) {
  const [weeklyOptedOut, setWeeklyOptedOut] = useState(false);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (open) {
      fetchOptStatus();
    }
  }, [open]);

  const fetchOptStatus = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/weekly-report/opt-status`, {
        headers: api.getHeaders(),
      });
      const data = await res.json();
      setWeeklyOptedOut(data.opted_out || false);
    } catch {
      // default
    } finally {
      setLoading(false);
    }
  };

  const toggleWeeklyReport = async (enabled) => {
    const optOut = !enabled;
    try {
      const res = await fetch(`${API_URL}/api/weekly-report/opt-out?opt_out=${optOut}`, {
        method: 'PUT',
        headers: api.getHeaders(),
      });
      if (res.ok) {
        setWeeklyOptedOut(optOut);
        toast.success(enabled ? 'Weekly report enabled' : 'Weekly report disabled');
      }
    } catch {
      toast.error('Failed to update preference');
    }
  };

  const sendNow = async () => {
    setSending(true);
    try {
      const res = await fetch(`${API_URL}/api/weekly-report/send`, {
        method: 'POST',
        headers: api.getHeaders(),
      });
      const data = await res.json();
      if (res.ok) {
        toast.success(`Report sent to ${data.email}`);
      } else {
        toast.error(data.detail || 'Failed to send report');
      }
    } catch {
      toast.error('Failed to send report');
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-slate-900 border-slate-800 text-white max-w-sm rounded-2xl" hideCloseButton>
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2 text-lg">
            <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center">
              <Bell className="h-4 w-4 text-white" />
            </div>
            Reminders
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-3 mt-2">
          {/* Weekly Progress Report */}
          <div className="bg-slate-800/60 rounded-xl p-4 border border-slate-700/50" data-testid="weekly-report-toggle">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-blue-500 to-violet-600 flex items-center justify-center">
                  <Mail className="h-4.5 w-4.5 text-white" />
                </div>
                <div>
                  <p className="text-sm font-medium text-slate-100">Weekly Progress Report</p>
                  <p className="text-xs text-slate-400">Email every Monday</p>
                </div>
              </div>
              {loading ? (
                <Loader2 className="h-4 w-4 animate-spin text-slate-400" />
              ) : (
                <Switch
                  checked={!weeklyOptedOut}
                  onCheckedChange={toggleWeeklyReport}
                  data-testid="weekly-report-switch"
                />
              )}
            </div>
            <p className="text-xs text-slate-500 mt-1 ml-12">
              Nutrition, walking & workout stats summary
            </p>
          </div>

          {/* Send Now Button */}
          <button
            onClick={sendNow}
            disabled={sending}
            className="w-full flex items-center justify-between bg-slate-800/40 hover:bg-slate-800/70 rounded-xl p-4 border border-slate-700/30 transition-colors group"
            data-testid="send-report-now-btn"
          >
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center">
                {sending ? (
                  <Loader2 className="h-4 w-4 animate-spin text-white" />
                ) : (
                  <Mail className="h-4 w-4 text-white" />
                )}
              </div>
              <div className="text-left">
                <p className="text-sm font-medium text-slate-100">Send Report Now</p>
                <p className="text-xs text-slate-400">Get this week's summary instantly</p>
              </div>
            </div>
            <ChevronRight className="h-4 w-4 text-slate-500 group-hover:text-slate-300 transition-colors" />
          </button>
        </div>

        {/* Close */}
        <Button
          variant="ghost"
          className="w-full mt-2 text-slate-400 hover:text-white hover:bg-slate-800"
          onClick={() => onOpenChange(false)}
          data-testid="reminder-close-btn"
        >
          Close
        </Button>
      </DialogContent>
    </Dialog>
  );
}
